import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { COMPANY } from "@/lib/api";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/projects", label: "Projects" },
  { to: "/amenities", label: "Amenities" },
  { to: "/location", label: "Location" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#0a0a0b]/85 backdrop-blur-xl border-b border-[#1f1f22]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-24 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 group">
          <img src="/logo_mark.png" alt="Shree Aryavarta Developers" className="h-16 w-16 object-contain" />
          <span className="font-serif text-[17px] leading-tight tracking-tight">
            <span className="block text-white">{COMPANY.split(" ")[0]} {COMPANY.split(" ")[1]}</span>
            <span className="block text-[10px] tracking-[0.3em] uppercase text-[#8f8f94]">Developers</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm tracking-wide transition-colors ${
                  isActive ? "text-[#c5a059]" : "text-[#cfcfd2] hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="navbar-cta-enquire"
            className="btn-gold px-5 py-2.5 rounded-full text-sm font-medium"
          >
            Get Launch Updates
          </Link>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0a0a0b] border-t border-[#1f1f22]" data-testid="navbar-mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2.5 text-sm border-b border-[#1f1f22] ${
                    isActive ? "text-[#c5a059]" : "text-[#cfcfd2]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-cta-enquire"
              className="btn-gold px-5 py-3 rounded-full text-sm font-medium text-center mt-3"
            >
              Get Launch Updates
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
